import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const RECOVERY_KEY = '4lc_password_recovery'

const AuthContext = createContext({})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [clientRecord, setClientRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [passwordRecovery, setPasswordRecovery] = useState(
    () => sessionStorage.getItem(RECOVERY_KEY) === 'true'
  )

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else {
        sessionStorage.removeItem(RECOVERY_KEY)
        setPasswordRecovery(false)
        setLoading(false)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          sessionStorage.setItem(RECOVERY_KEY, 'true')
          setPasswordRecovery(true)
        }
        if (event === 'SIGNED_OUT') {
          sessionStorage.removeItem(RECOVERY_KEY)
          setPasswordRecovery(false)
        }
        setUser(session?.user ?? null)
        if (session?.user) fetchProfile(session.user.id)
        else {
          setProfile(null)
          setClientRecord(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const fetchProfile = async (userId) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()
    setProfile(data)

    if (data) {
      supabase.from('profiles').update({ last_active_at: new Date().toISOString() }).eq('id', userId).then(() => {})
    }

    // If client role, also fetch their client record
    if (data?.role === 'client' && data?.client_id) {
      const { data: clientData } = await supabase
        .from('clients')
        .select('*')
        .eq('id', data.client_id)
        .maybeSingle()

      // Archived clients are locked out of the portal.
      // Local scope: end the session in THIS browser. The same check runs on
      // every load, so another device locks itself out too — there is no need
      // to reach across and revoke sessions we are not looking at.
      if (clientData?.status === 'archived') {
        await supabase.auth.signOut({ scope: 'local' })
        setProfile(null)
        setClientRecord(null)
        setLoading(false)
        return
      }

      setClientRecord(clientData)
    } else {
      setClientRecord(null)
    }

    setLoading(false)
  }

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  // SIGN OUT THIS BROWSER, NOT EVERY DEVICE
  //
  // supabase-js defaults signOut() to GLOBAL scope, which revokes every
  // refresh token the user holds anywhere. Its own docs: "By default,
  // signOut() uses the global scope, which signs out all other sessions that
  // the user is logged into as well."
  //
  // So signing out on a laptop silently killed the same user's session on
  // their phone, and anyone sharing a login was booted mid-task. Observed
  // 2026-10-05: a sign-out in one browser ended Mike's session in another
  // while he was running the AI plan builder, which surfaced as the API's
  // "Invalid session" — a dead token on a request that looked fine.
  //
  // Local scope clears this browser's stored session and leaves other devices
  // alone, which is what a Sign Out button means to the person clicking it.
  const signOut = async () => {
    sessionStorage.removeItem(RECOVERY_KEY)
    setPasswordRecovery(false)
    await supabase.auth.signOut({ scope: 'local' })
  }

  const clearPasswordRecovery = () => {
    sessionStorage.removeItem(RECOVERY_KEY)
    setPasswordRecovery(false)
  }

  return (
    <AuthContext.Provider value={{ user, profile, clientRecord, loading, passwordRecovery, clearPasswordRecovery, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
